import { createContext, useContext, useEffect, useRef, useState } from "react";

import { suppliers } from "../../components/salesDocuments-v2/salesDocuments.constants";
import {
  ElectricityData,
  GasData,
  formatMeterDetails,
} from "../../services/salesDocuments-v2.service";
import { Meter, UnitRate } from "../../types/salesDocuments.types";

interface Kva {
  kvaCharge: number;
  kvaCapacity: number;
  kvaChargeFrequency: string;
}

export interface MeterContextType {
  zohoMeterId: string;
  supplier: string;
  supplierName: string;
  product: string;
  nonCommodityPrompt: string;
  nonCommodityCost: string;
  meterId: string;
  hhMeter: string;
  standingCharge: string;
  commission: string;
  fitCharge: string;
  fitChargeUnit: string;
  totalConsumption: string;
  estimatedAnnualCost: string;
  consumptionMeasurement: string;
  kva: Kva | null;
  includesKva: string;
  dayRate: UnitRate;
  nightRate: UnitRate;
  eveningWeekendRate: UnitRate;
  eveningWeekendNightRate: UnitRate;
  startDate: string;
  endDate: string;
  contractType: string;
  passthrough: string;
  lockedFields: Set<string>;
  supplyAddress: string[];
  apiDataLoaded: boolean;
  mprn: string;
  mpanTopLine: string;
  mpanBottomLine: string;
  upliftRate: string;
  scUpliftRate: string;
  manualCalculation: boolean;
  oneShotConsumptionUpdate: boolean;
  autofillFromApi: (meterId: string) => Promise<boolean>;
  handleEstimatedAnnualCostChange: (value: string) => void;
  setZohoMeterId: (value: string) => void;
  setSupplier: (value: string) => void;
  setSupplierName: (value: string) => void;
  handleSupplierChange: (supplierId: string) => void;
  handleProductChange: (productId: string) => void;
  setNonCommodityCost: (value: string) => void;
  setMeterId: (value: string) => void;
  setHhMeter: (value: string) => void;
  setStandingCharge: (value: string) => void;
  setCommission: (value: string) => void;
  setFitCharge: (value: string) => void;
  setFitChargeUnit: (value: string) => void;
  setTotalConsumption: (value: string) => void;
  setEstimatedAnnualCost: (value: string) => void;
  setConsumptionMeasurement: (value: string) => void;
  setIncludesKva: (value: string) => void;
  setDayRate: (value: UnitRate) => void;
  setNightRate: (value: UnitRate) => void;
  setEveningWeekendRate: (value: UnitRate) => void;
  setEveningWeekendNightRate: (value: UnitRate) => void;
  setStartDate: (value: string) => void;
  setEndDate: (value: string) => void;
  setContractType: (value: string) => void;
  setPassthrough: (value: string) => void;
  setApiDataLoaded: (value: boolean) => void;
  setMprn: (value: string) => void;
  setMpanTopLine: (value: string) => void;
  setMpanBottomLine: (value: string) => void;
  setKva: (value: Kva | null) => void;
  setUpliftRate: (value: string) => void;
  setScUpliftRate: (value: string) => void;
  setManualCalculation: (value: boolean) => void;
  setOneShotConsumptionUpdate: (value: boolean) => void;
}

const MeterContext = createContext<MeterContextType | null>(null);

export const useMeterContext = () => {
  const ctx = useContext(MeterContext);
  if (!ctx) throw new Error("useMeterContext must be used inside MeterContextProvider");
  return ctx;
};

export const MeterContextProvider = ({
  meter,
  children,
  registerPayloadGetter,
}: {
  meter: Meter;
  children: React.ReactNode;
  registerPayloadGetter: (meterId: number, getter: () => ElectricityData | GasData | null) => void;
}) => {
  const [zohoMeterId, setZohoMeterId] = useState("");
  const [supplier, setSupplier] = useState("other");
  const [supplierName, setSupplierName] = useState("");
  const [product, setProduct] = useState("");
  const [nonCommodityPrompt, setNonCommodityPrompt] = useState("");
  const [nonCommodityCost, setNonCommodityCost] = useState("");
  const [meterId, setMeterId] = useState("");
  const [hhMeter, setHhMeter] = useState("no");
  const [standingCharge, setStandingCharge] = useState("");
  const [commission, setCommission] = useState("");
  const [fitCharge, setFitCharge] = useState("");
  const [fitChargeUnit, setFitChargeUnit] = useState("");
  const [totalConsumption, setTotalConsumption] = useState("");
  const [estimatedAnnualCost, setEstimatedAnnualCost] = useState("");
  const [consumptionMeasurement, setConsumptionMeasurement] = useState("AQ");
  const [includesKva, setIncludesKva] = useState("na");
  const [dayRate, setDayRate] = useState<UnitRate>({
    rate: "",
    consumption: "",
  });
  const [nightRate, setNightRate] = useState<UnitRate>({
    rate: "",
    consumption: "",
  });
  const [eveningWeekendRate, setEveningWeekendRate] = useState<UnitRate>({
    rate: "",
    consumption: "",
  });
  const [eveningWeekendNightRate, setEveningWeekendNightRate] = useState<UnitRate>({
    rate: "",
    consumption: "",
  });
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [contractType, setContractType] = useState("");
  const [passthrough, setPassthrough] = useState("");
  const [lockedFields, setLockedFields] = useState<Set<string>>(new Set());
  const [supplyAddress, setSupplyAddress] = useState<string[]>([]);
  const [apiDataLoaded, setApiDataLoaded] = useState<boolean>(false);
  const [mpanTopLine, setMpanTopLine] = useState("");
  const [mpanBottomLine, setMpanBottomLine] = useState("");
  const [mprn, setMprn] = useState("");
  const [kva, setKva] = useState<Kva | null>(null);
  const [upliftRate, setUpliftRate] = useState("");
  const [scUpliftRate, setScUpliftRate] = useState("");
  const [manualCalculation, setManualCalculation] = useState(false);

  // Use this to update the rates ONCE when we load data
  const [oneShotConsumptionUpdate, setOneShotConsumptionUpdate] = useState(false);

  // One-shot effect: when annualConsumption loads, backfill if single rate and oneshot hasn't run
  useEffect(() => {
    if (manualCalculation || !totalConsumption || oneShotConsumptionUpdate) return;

    const rates = [dayRate, nightRate, eveningWeekendRate, eveningWeekendNightRate];

    // Count rates that have a rate value
    const activeRatesCount = rates.filter((r) => r.rate !== "").length;

    // If exactly one or no rates with a rate value AND consumption is still empty, backfill
    if (activeRatesCount <= 1 && dayRate.consumption === "") {
      // Deliberate one-shot sync from API-loaded totalConsumption into the day
      // rate, guarded by oneShotConsumptionUpdate so it can't cascade
      setDayRate((prev) => ({
        ...prev,
        consumption: totalConsumption,
      }));
      setOneShotConsumptionUpdate(true);
    }
  }, [
    totalConsumption,
    dayRate,
    nightRate,
    eveningWeekendRate,
    eveningWeekendNightRate,
    oneShotConsumptionUpdate,
    manualCalculation,
  ]);

  // Single effect that does all calculations
  useEffect(() => {
    const rates = [dayRate, nightRate, eveningWeekendRate, eveningWeekendNightRate];
    if (manualCalculation) return;

    // Calculate total consumption
    const totalCons = rates
      .filter((r) => r.consumption !== "" && r.rate !== "")
      .reduce((sum, r) => sum + parseFloat(r.consumption), 0);

    // Calculate estimated annual cost
    const rateTotal = rates
      .filter((r) => r.rate !== "" && r.consumption !== "")
      .reduce((sum, r) => sum + parseFloat(r.rate) * parseFloat(r.consumption), 0);
    const standingChargeTotal = parseFloat(standingCharge) * 365 || 0;
    const estimatedAnnualCostValue =
      rateTotal + standingChargeTotal > 0
        ? ((rateTotal + standingChargeTotal) / 100).toFixed(2)
        : "0";

    // Remove the backfill logic entirely - let the one-shot handle it

    // Always update these
    // Keep the existing total (API-loaded or typed) when no rates contribute.
    // These stay as state rather than derived values because manual calculation
    // mode, the API autofill and the one-shot backfill above all write to them too
    setTotalConsumption((prev) => (totalCons > 0 ? totalCons.toFixed(5) : prev));
    setEstimatedAnnualCost(estimatedAnnualCostValue);
  }, [
    dayRate,
    nightRate,
    eveningWeekendRate,
    eveningWeekendNightRate,
    standingCharge,
    manualCalculation,
  ]);

  const handleEstimatedAnnualCostChange = (value: string) => {
    setEstimatedAnnualCost(value);
  };

  const resetState = () => {
    setSupplier("other");
    setSupplierName("");
    setProduct("");
    setNonCommodityPrompt("");
    setNonCommodityCost("");
    setHhMeter("no");
    setStandingCharge("");
    setCommission("");
    setFitCharge("");
    setFitChargeUnit("");
    setTotalConsumption("");
    setEstimatedAnnualCost("");
    setConsumptionMeasurement("AQ");
    setIncludesKva("no");
    setDayRate({ rate: "", consumption: "" });
    setNightRate({ rate: "", consumption: "" });
    setEveningWeekendRate({ rate: "", consumption: "" });
    setEveningWeekendNightRate({ rate: "", consumption: "" });
    setStartDate("");
    setEndDate("");
    setContractType("");
    setPassthrough("");
    setLockedFields(new Set());
    setSupplyAddress([]);
    setApiDataLoaded(false);
    setMpanTopLine("");
    setMpanBottomLine("");
    setMprn("");
    setKva(null);
    setUpliftRate("");
    setScUpliftRate("");
  };

  const autofillFromApi = async (meterId: string): Promise<boolean> => {
    setApiDataLoaded(false);
    resetState();
    const result = await formatMeterDetails(meterId);
    if (!result.success) {
      // TODO: This assumes 404, needs more robust error handling
      // if (result.statusCode === 404) {
      //   setError("Customer not found");
      // } else {
      //   setError(`Failed to load customer: ${result.error}`);
      // }
      return false;
    }

    const data = result.data;

    setZohoMeterId(meterId);

    const locked = new Set<string>();

    const setIfPresent = (
      key: string,
      value: string | number | undefined | null,
      setter: (v: string) => void,
    ) => {
      if (value !== undefined && value !== null) {
        setter(String(value));
        locked.add(key);
        setLockedFields(locked);
      }
    };

    setIfPresent("mpanTopLine", data.mpanTopLine, setMpanTopLine);
    setIfPresent("mpanBottomLine", data.mpanBottomLine, setMpanBottomLine);
    setIfPresent("mprn", data.mprn, setMprn);
    // NOTE: All hard-coded suppliers are single word, so split to take one word
    const newSupplier = data.newSupplier;
    if (newSupplier) {
      const matched = suppliers.find(
        (s) => s.label.toLowerCase().split(" ")[0] === newSupplier.toLowerCase(),
      );
      handleSupplierChange(matched?.value ?? "other");
      if (!matched) {
        setSupplierName(newSupplier);
      }
    }
    setIfPresent("totalConsumption", data.annualConsumption?.toString(), setTotalConsumption);
    if (data.dayUnitRate) {
      setDayRate({ rate: String(data.dayUnitRate), consumption: "" });
      locked.add("dayRate");
    }
    if (data.nightUnitRate) {
      setNightRate({ rate: String(data.nightUnitRate), consumption: "" });
      locked.add("nightRate");
    }
    if (data.eveningWeekendRate) {
      setEveningWeekendRate({
        rate: String(data.eveningWeekendRate),
        consumption: "",
      });
      locked.add("eveningWeekendRate");
    }
    if (data.eveningWeekendNightRate) {
      setEveningWeekendNightRate({
        rate: String(data.eveningWeekendNightRate),
        consumption: "",
      });
      locked.add("eveningWeekendNightRate");
    }
    setIfPresent("standingCharge", data.standingCharge?.toString(), setStandingCharge);
    setIfPresent("hhMeter", data.mpanTopLine?.startsWith("00") ? "yes" : "no", setHhMeter);
    setIfPresent("startDate", data.startDate, setStartDate);
    setIfPresent("endDate", data.endDate, setEndDate);
    setIfPresent("contractType", data.contractType, setContractType);
    setIfPresent("passthrough", data.passthrough, setPassthrough);
    setIfPresent("commission", data.commission, setCommission);
    setIfPresent("upliftRate", data.upliftRate, setUpliftRate);
    setIfPresent("scUpliftRate", data.scUpliftRate, setScUpliftRate);

    // supply address
    const address = [data.street, data.town, data.county, data.country, data.postcode].filter(
      (part): part is string => Boolean(part),
    );
    setSupplyAddress(address);
    if (address.length) locked.add("supplyAddress");

    // Set kva stuff
    if (data.kvaCharge && data.kvaCapacity && data.kvaChargeFrequency) {
      setKva({
        kvaCharge: data.kvaCharge,
        kvaCapacity: data.kvaCapacity,
        kvaChargeFrequency: data.kvaChargeFrequency,
      });
      locked.add("kva");
    }

    setLockedFields(locked);
    setApiDataLoaded(true);
    return true;
  };

  const getMeterPayloadRef = useRef<() => ElectricityData | GasData | null>(() => null);

  useEffect(() => {
    registerPayloadGetter(meter.id, () => getMeterPayloadRef.current());
  }, [meter.id, registerPayloadGetter]);

  const handleSupplierChange = (supplierId: string) => {
    setSupplier(supplierId);
    // Find first product available for that supplier because that's the default
    const firstProduct = suppliers.find((s) => s.value === supplierId)?.products[0] ?? "";
    if (firstProduct) {
      setProduct(firstProduct.productName);
      // Trigger handle product change so we can set cost / prompt if necessary
      handleProductChange(firstProduct.id, supplierId);
    } else {
      setNonCommodityCost("");
      setNonCommodityPrompt("");
      setProduct("");
    }
    // We add an exception here because NPower is special and different and has a different Fit charge measurement
    if (supplierId === "npower") {
      setFitChargeUnit("£/kWh");
    } else {
      setFitChargeUnit("p/kWh");
    }
  };

  const handleProductChange = (productId: string, activeSupplierId: string = supplier) => {
    setProduct(productId);
    const productData = suppliers
      .find((s) => s.value === activeSupplierId)
      ?.products.find((p) => p.id === productId);
    setNonCommodityCost(productData?.productStatement ?? "");
    setNonCommodityPrompt(productData?.prompt ?? "");
  };

  // Keep the payload getter pointing at this render's values - assigned in an
  // effect (no deps, runs after every render) since refs can't be written during render
  useEffect(() => {
    getMeterPayloadRef.current = () => {
      if (meterId.length !== 18) return null;

      if (meter.fuelType === "electricity") {
        return {
          zoho_meter_id: zohoMeterId,
          start_date: startDate,
          end_date: endDate,
          new_supplier:
            supplier === "other"
              ? supplierName
              : (suppliers.find((s) => s.value === supplier)?.label ?? supplier),
          contract_type: contractType,
          annual_consumption: parseFloat(totalConsumption) || 0,
          estimated_annual_cost: parseFloat(estimatedAnnualCost) || 0,
          supply_address: supplyAddress,
          ...(product && {
            product_name:
              suppliers.find((s) => s.value === supplier)?.products.find((p) => p.id === product)
                ?.productName ?? product,
          }),
          ...(passthrough && { passthrough: passthrough }),
          ...(standingCharge && { standing_charge: parseFloat(standingCharge) }),
          ...(nonCommodityCost && { non_commodity_cost: nonCommodityCost }),
          commission: commission,
          ...(upliftRate && { uplift_rate: upliftRate }),
          ...(scUpliftRate && { sc_uplift_rate: scUpliftRate }),
          mpan: mpanBottomLine,
          ...(fitCharge && { fit_charge: fitCharge }),
          ...(fitChargeUnit && { fit_charge_unit: fitChargeUnit }),
          consumption_measurement: consumptionMeasurement,
          estimate_includes_kva: includesKva,
          ...(kva && {
            kva: {
              capacity: kva.kvaCapacity,
              charge: kva.kvaCharge,
              frequency: kva.kvaChargeFrequency,
            },
          }),
          ...(dayRate.rate && {
            day_unit_rate: {
              rate: dayRate.rate,
              consumption: dayRate.consumption,
            },
          }),
          ...(nightRate.rate && {
            night_unit_rate: {
              rate: nightRate.rate,
              consumption: nightRate.consumption,
            },
          }),
          ...(eveningWeekendRate.rate && {
            evening_weekend_rate: {
              rate: eveningWeekendRate.rate,
              consumption: eveningWeekendRate.consumption,
            },
          }),
          ...(eveningWeekendNightRate.rate && {
            evening_weekend_night_rate: {
              rate: eveningWeekendNightRate.rate,
              consumption: eveningWeekendNightRate.consumption,
            },
          }),
        };
      } else {
        return {
          zoho_meter_id: zohoMeterId,
          start_date: startDate,
          end_date: endDate,
          new_supplier:
            supplier === "other"
              ? supplierName
              : (suppliers.find((s) => s.value === supplier)?.label ?? supplier),
          contract_type: contractType,
          annual_consumption: parseFloat(totalConsumption) || 0,
          estimated_annual_cost: parseFloat(estimatedAnnualCost) || 0,
          supply_address: supplyAddress,
          ...(product && {
            product_name:
              suppliers.find((s) => s.value === supplier)?.products.find((p) => p.id === product)
                ?.productName ?? product,
          }),
          ...(passthrough && { passthrough: passthrough }),
          ...(standingCharge && { standing_charge: parseFloat(standingCharge) }),
          ...(nonCommodityCost && { non_commodity_cost: nonCommodityCost }),
          commission: commission,
          ...(upliftRate && { uplift_rate: upliftRate }),
          ...(scUpliftRate && { sc_uplift_rate: scUpliftRate }),
          mprn: mprn,
          unit_rate: { rate: dayRate.rate, consumption: dayRate.consumption },
        };
      }
    };
  });

  return (
    <MeterContext.Provider
      value={{
        zohoMeterId,
        supplier,
        supplierName,
        product,
        nonCommodityPrompt,
        nonCommodityCost,
        meterId,
        hhMeter,
        commission,
        standingCharge,
        fitCharge,
        fitChargeUnit,
        totalConsumption,
        estimatedAnnualCost,
        consumptionMeasurement,
        includesKva,
        kva,
        dayRate,
        nightRate,
        eveningWeekendRate,
        eveningWeekendNightRate,
        startDate,
        endDate,
        contractType,
        passthrough,
        lockedFields,
        supplyAddress,
        apiDataLoaded,
        mprn,
        mpanTopLine,
        mpanBottomLine,
        upliftRate,
        scUpliftRate,
        manualCalculation,
        oneShotConsumptionUpdate,
        autofillFromApi,
        handleEstimatedAnnualCostChange,
        handleSupplierChange,
        handleProductChange,
        setZohoMeterId,
        setSupplier,
        setSupplierName,
        setNonCommodityCost,
        setMeterId,
        setHhMeter,
        setCommission,
        setStandingCharge,
        setFitCharge,
        setFitChargeUnit,
        setTotalConsumption,
        setEstimatedAnnualCost,
        setConsumptionMeasurement,
        setIncludesKva,
        setKva,
        setDayRate,
        setNightRate,
        setEveningWeekendRate,
        setEveningWeekendNightRate,
        setStartDate,
        setEndDate,
        setContractType,
        setPassthrough,
        setApiDataLoaded,
        setMprn,
        setMpanTopLine,
        setMpanBottomLine,
        setUpliftRate,
        setScUpliftRate,
        setManualCalculation,
        setOneShotConsumptionUpdate,
      }}
    >
      {children}
    </MeterContext.Provider>
  );
};
